import { Checkpoint, Config } from '@langchain/langgraph-sdk';
import { PlayCircleIcon } from '@langchain/untitled-ui-icons';
import { Alert } from '@mui/joy';

import React, { useCallback, useState } from 'react';
import useSWRMutation from 'swr/mutation';

import { CodeData } from '@/components/Code/Code';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/Select/Select';
import { Button } from '@/design-system';

import { useLangGraphClient } from '../../../api';
import { useGraphThreadHistoryMutate } from '../../../api/useGraphSwr';
import { SCHEMA_ERROR_MESSAGE } from '../../../constants';
import { GraphDataContext } from '../../../control/graph-data';
import {
  SchemaBundle,
  StateContext,
  UntypedInput,
} from '../../../control/state';
import { TraceLog } from '../../../data/misc';
import { useActiveThreadId } from '../../../hooks/useActiveThreadId';
import { getEditableNodes, useEnterSubmit } from '../../../utils';
import { RawRenderedToggle } from '../common/RawRenderedToggle';
import { SchemaEditor } from '../common/schema-editor';
import { defaultSchemaRenderer } from '../common/schema-renderer';

export const EditNodeState = (props: {
  traceLog: TraceLog;
  schema?: SchemaBundle;
  namespace: string | undefined;
  threadId: string;
  checkpoint: Checkpoint | string;
  onCancel: () => void;
  onDone: (
    config: Pick<Config, 'configurable'> & { checkpoint: Checkpoint }
  ) => void;
}) => {
  const state = React.useContext(StateContext);
  const graph = React.useContext(GraphDataContext);
  const [langgraph, apiUrl] = useLangGraphClient();
  const { threadId } = useActiveThreadId();
  const historySWRMutate = useGraphThreadHistoryMutate(threadId);

  const nodes = getEditableNodes(graph, props.namespace);
  const [asNode, setAsNode] = React.useState<string | undefined>(
    nodes.find((node) => node === props.traceLog.entry.name)
  );
  const [isAsNodeHidden] = useState(asNode != null);

  const [values, setValues] = React.useState<unknown>(
    'data' in props.traceLog.entry ? props.traceLog.entry.data.raw : undefined
  );

  const fork = useSWRMutation(
    [apiUrl, 'threads', props.threadId],
    async () => {
      const target =
        typeof props.checkpoint === 'string'
          ? { checkpointId: props.checkpoint }
          : props.checkpoint != null
          ? { checkpoint: props.checkpoint }
          : {};

      let data:
        | Awaited<ReturnType<typeof langgraph.threads.updateState>>
        | undefined;

      try {
        // TODO: validate if we need to pass all tasks (even those, that were not edited)
        data = await langgraph.threads.updateState(props.threadId, {
          values: [[values, asNode]],
          asNode: '__copy__',
          ...target,
        });
      } catch {
        data = await langgraph.threads.updateState(props.threadId, {
          values,
          asNode,
          ...target,
        });
      }

      // TODO: append the new state to updates
      // as we should know all the information upfront
      await historySWRMutate();
      return data as Pick<Config, 'configurable'> & { checkpoint: Checkpoint };
    },
    { onSuccess: props.onDone }
  );

  const onClickFork = useCallback(() => fork.trigger(), [fork]);
  useEnterSubmit(false, onClickFork);

  return (
    <div className="z-10 -mt-8 flex flex-col gap-2">
      <div className="flex w-full justify-end gap-2">
        {props.schema && <RawRenderedToggle variant="plain" />}
        {!isAsNodeHidden && (
          <div className="flex place-items-center gap-2">
            <span className="text-sm text-tertiary">As Node</span>
            <Select value={asNode} onValueChange={setAsNode}>
              <SelectTrigger>
                <SelectValue placeholder="Select node" />
              </SelectTrigger>
              <SelectContent>
                {nodes.map((node) => (
                  <SelectItem key={node} value={node}>
                    {node}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <Button
          type="button"
          onClick={props.onCancel}
          variant="outlined"
          color="secondary"
        >
          Cancel
        </Button>
        <Button
          type="button"
          onClick={onClickFork}
          loading={fork.isMutating}
          leftDecorator={PlayCircleIcon}
        >
          Fork
        </Button>
      </div>
      <div className="rounded-md border border-secondary bg-secondary p-3">
        {state.viewRaw || !props.schema ? (
          <>
            {!props.schema && (
              <Alert color="warning">
                <span>{SCHEMA_ERROR_MESSAGE}</span>
              </Alert>
            )}
            <div className="mt-2 rounded-md border border-secondary bg-background">
              <CodeData value={values} onChange={setValues} />
            </div>
          </>
        ) : (
          <SchemaEditor
            skipEmpty
            readOnly={false}
            input={values as UntypedInput}
            schema={
              props.traceLog.entry.type === 'input'
                ? props.schema.input ?? props.schema.state
                : props.schema.state
            }
            rootSchema={
              props.traceLog.entry.type === 'input'
                ? props.schema.inputRoot ?? props.schema.stateRoot
                : props.schema.stateRoot
            }
            onChange={setValues}
            renderer={defaultSchemaRenderer}
            options={{ hideDeleteKey: true }}
          />
        )}
      </div>
    </div>
  );
};
